import { createTheme } from '@mui/material/styles';

// Quiet Luxury Palette - Refined & Expensive
const palette = {
  primary: {
    main: '#7C3AED', // Deep Royal Violet
    light: '#A78BFA',
    dark: '#5B21B6',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#64748b', // Cool Slate
    light: '#94a3b8',
    dark: '#334155',
    contrastText: '#ffffff',
  },
  background: {
    default: '#F8FAFC', // Premium Cloud White
    paper: '#FFFFFF',   // Pure White Cards
    subtle: '#F1F5F9',  // Very light grey for secondary areas
  },
  text: {
    primary: '#1e293b', // Slate 800 - Soft Black
    secondary: '#64748b', // Slate 500
    disabled: '#94a3b8',
  },
  success: {
    main: '#10B981', // Emerald
    light: '#34D399',
    dark: '#059669',
  },
  warning: {
    main: '#F59E0B', // Amber
    light: '#FBBF24',
    dark: '#D97706',
  },
  error: {
    main: '#EF4444', // Rose
    light: '#F87171',
    dark: '#B91C1C',
  },
  divider: 'rgba(0, 0, 0, 0.06)',
  action: {
    hover: 'rgba(124, 58, 237, 0.04)',
    selected: 'rgba(124, 58, 237, 0.08)',
  },
};

const theme = createTheme({
  palette,
  shape: {
    borderRadius: 16, // Modern, friendly curves
  },
  typography: {
    fontFamily: '"Inter", "Outfit", sans-serif',
    h1: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 600,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontFamily: '"Inter", sans-serif',
    },
    body1: {
      fontFamily: '"Inter", sans-serif',
      lineHeight: 1.6,
    },
    body2: {
      fontFamily: '"Inter", sans-serif',
      color: palette.text.secondary,
      lineHeight: 1.5,
    },
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(148, 163, 184, 0.05)', // Elevation 1 (Subtle)
    '0px 4px 10px rgba(148, 163, 184, 0.08)', // Elevation 2 (Card Default)
    '0px 8px 20px rgba(148, 163, 184, 0.12)', // Elevation 3 (Hover/Dropdown)
    '0px 12px 30px rgba(148, 163, 184, 0.15)', // Elevation 4 (Modal)
    ...Array(21).fill('none'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '8px 20px',
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)',
          '&:active': {
            transform: 'scale(0.96)',
          },
          '&:disabled': {
            color: '#ffffff',
          },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
          boxShadow: '0px 4px 12px rgba(124, 58, 237, 0.3)',
          '&:hover': {
            boxShadow: '0px 8px 20px rgba(124, 58, 237, 0.4)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)', // Very soft ambient shadow
          border: '1px solid rgba(255, 255, 255, 0.5)',
          background: '#ffffff',
          overflow: 'hidden',
          transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0px 12px 30px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FFFFFF',
          borderRight: '1px solid rgba(0, 0, 0, 0.12)', // Increased contrast
          boxShadow: '4px 0 24px rgba(0, 0, 0, 0.04)', // subtle shadow to lift it
          boxSizing: 'border-box',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 16,
        },
        elevation1: {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${palette.divider}`,
          padding: '16px 24px',
        },
        head: {
          fontWeight: 600,
          color: palette.text.secondary,
          backgroundColor: palette.background.subtle,
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          letterSpacing: '0.05em',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
        },
        filled: {
          backgroundColor: palette.background.subtle,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: palette.background.subtle,
            transition: 'all 0.2s ease',
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover': {
              backgroundColor: '#fff',
              boxShadow: '0 0 0 2px rgba(124, 58, 237, 0.1)',
            },
            '&.Mui-focused': {
              backgroundColor: '#fff',
              boxShadow: '0 0 0 2px rgba(124, 58, 237, 0.2)',
              '& fieldset': {
                borderColor: palette.primary.main,
              },
            },
          },
        },
      },
    },
  },
});

export default theme;